import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Save, Facebook, Linkedin, Twitter, Instagram, Youtube, Globe,
  Plus, Trash2, CheckCircle2, ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";
import { inputCls } from "../../components/forms/FormBits";

const FIELDS = [
  ["company_name", "Company name"], ["tagline", "Tagline"], ["logo_url", "Logo URL"],
  ["email", "Contact email"], ["phone", "Contact phone"], ["website", "Website"],
  ["footer_note", "Brief footer note"],
];

// Scalable catalog — add a new known platform by appending ONE entry here.
// Anything not in the catalog is treated as a custom channel (runtime, no code).
const SOCIAL_CATALOG = [
  { key: "facebook", label: "Facebook", icon: Facebook, placeholder: "https://facebook.com/your-page" },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, placeholder: "https://linkedin.com/company/your-company" },
  { key: "twitter", label: "X (Twitter)", icon: Twitter, placeholder: "https://x.com/your-handle" },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/your-handle" },
  { key: "youtube", label: "YouTube", icon: Youtube, placeholder: "https://youtube.com/@your-channel" },
];
const CATALOG_KEYS = new Set(SOCIAL_CATALOG.map((s) => s.key));

const isValidUrl = (u) => /^(https?:\/\/)?[^\s.]+\.[^\s]{2,}/.test((u || "").trim());

export default function BrandSettings() {
  const [brand, setBrand] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/brand").then(({ data }) => setBrand(data)).catch(() => toast.error("Failed to load brand")); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/brand", brand);
      setBrand(data);
      toast.success("Settings updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!brand) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;

  const links = Array.isArray(brand.social_links) ? brand.social_links : [];
  const getUrl = (key) => links.find((s) => s.key === key)?.url || "";
  const customs = links.filter((s) => !CATALOG_KEYS.has(s.key));

  const upsert = (key, label, url) => setBrand((b) => {
    const list = Array.isArray(b.social_links) ? [...b.social_links] : [];
    const i = list.findIndex((s) => s.key === key);
    if (i >= 0) list[i] = { key, label, url };
    else list.push({ key, label, url });
    return { ...b, social_links: list };
  });
  const updateCustom = (idx, patch) => setBrand((b) => {
    const list = [...(b.social_links || [])];
    const target = customs[idx];
    const realIdx = list.findIndex((s) => s === target);
    if (realIdx >= 0) list[realIdx] = { ...list[realIdx], ...patch };
    return { ...b, social_links: list };
  });
  const addCustom = () => setBrand((b) => ({
    ...b,
    social_links: [...(b.social_links || []), { key: `channel-${Date.now()}`, label: "", url: "" }],
  }));
  const removeLink = (predicate) => setBrand((b) => ({
    ...b,
    social_links: (b.social_links || []).filter((s) => !predicate(s)),
  }));

  const StatusBadge = ({ active }) => (
    active
      ? <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2.5 py-0.5 text-[11px] font-semibold text-success" data-testid="social-status-connected"><CheckCircle2 className="h-3 w-3" /> Connected</span>
      : <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-semibold text-charcoal/50" data-testid="social-status-disconnected">Not Connected</span>
  );

  return (
    <div data-testid="brand-panel">
      <h2 className="mb-1 font-serif text-2xl text-navy">Brand &amp; Social</h2>
      <p className="mb-5 text-sm text-muted-foreground">These settings drive client-facing documents and the public site. No brand or social details are hard-coded.</p>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4 rounded-2xl border border-border bg-white p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, label]) => (
              <div key={k} className={k === "footer_note" ? "sm:col-span-2" : ""}>
                <label className="mb-1 block text-xs font-semibold text-charcoal/70">{label}</label>
                <input className={inputCls} value={brand[k] || ""} onChange={(e) => setBrand((b) => ({ ...b, [k]: e.target.value }))} data-testid={`brand-${k}`} />
              </div>
            ))}
            {[["primary_color", "Primary colour"], ["accent_color", "Accent colour"]].map(([k, label]) => (
              <div key={k}>
                <label className="mb-1 block text-xs font-semibold text-charcoal/70">{label}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={brand[k] || "#000000"} onChange={(e) => setBrand((b) => ({ ...b, [k]: e.target.value }))} className="h-10 w-12 rounded border border-border" data-testid={`brand-${k}`} />
                  <input className={inputCls} value={brand[k] || ""} onChange={(e) => setBrand((b) => ({ ...b, [k]: e.target.value }))} />
                </div>
              </div>
            ))}
          </div>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-70" data-testid="brand-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings
          </button>
        </div>
        {/* Live preview */}
        <div className="rounded-2xl border border-border bg-white p-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Preview</p>
          <div className="flex items-center gap-3 rounded-xl p-4 text-white" style={{ background: brand.primary_color }}>
            {brand.logo_url && <img src={brand.logo_url} alt="logo" className="h-11 w-11 rounded-full object-cover ring-1 ring-white/20" />}
            <div>
              <p className="font-serif">{brand.company_name}</p>
              <p className="text-xs" style={{ color: brand.accent_color }}>{brand.tagline}</p>
            </div>
          </div>
          <button className="mt-4 w-full rounded-lg py-2 text-sm font-semibold text-white" style={{ background: brand.accent_color }}>Accent button</button>
        </div>
      </div>

      {/* Social Media integration */}
      <div className="mt-6 rounded-2xl border border-border bg-white p-6" data-testid="social-panel">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-gold" />
          <h3 className="font-serif text-lg text-navy">Social Media</h3>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Add a valid link to connect a platform — it activates automatically and appears on the public site. Leave blank to keep it Not Connected.
        </p>

        <div className="mt-5 space-y-3">
          {SOCIAL_CATALOG.map(({ key, label, icon: Icon, placeholder }) => {
            const url = getUrl(key);
            const active = isValidUrl(url);
            return (
              <div key={key} className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 sm:flex-row sm:items-center" data-testid={`social-row-${key}`}>
                <div className="flex w-full items-center gap-3 sm:w-52 sm:shrink-0">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-navy/5 text-navy"><Icon className="h-4.5 w-4.5" /></span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-charcoal">{label}</p>
                    <StatusBadge active={active} />
                  </div>
                </div>
                <div className="flex flex-1 items-center gap-2">
                  <input
                    className={inputCls}
                    placeholder={placeholder}
                    value={url}
                    onChange={(e) => upsert(key, label, e.target.value)}
                    data-testid={`social-input-${key}`}
                  />
                  {active && (
                    <a href={url.startsWith("http") ? url : `https://${url}`} target="_blank" rel="noreferrer" className="shrink-0 rounded-lg border border-border p-2 text-navy hover:border-navy" title="Open link" data-testid={`social-open-${key}`}>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}

          {/* Custom / other channels — added at runtime, no code changes */}
          {customs.map((c, idx) => {
            const active = isValidUrl(c.url);
            return (
              <div key={c.key} className="flex flex-col gap-3 rounded-xl border border-dashed border-border bg-background p-4 sm:flex-row sm:items-center" data-testid={`social-custom-${idx}`}>
                <div className="flex w-full items-center gap-3 sm:w-52 sm:shrink-0">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gold/10 text-gold"><Globe className="h-4.5 w-4.5" /></span>
                  <div className="min-w-0 flex-1">
                    <input
                      className="w-full rounded-md border border-border bg-white px-2 py-1 text-sm font-semibold text-charcoal focus:border-navy focus:outline-none"
                      placeholder="Channel name"
                      value={c.label}
                      onChange={(e) => updateCustom(idx, { label: e.target.value })}
                      data-testid={`social-custom-label-${idx}`}
                    />
                    <div className="mt-1"><StatusBadge active={active} /></div>
                  </div>
                </div>
                <div className="flex flex-1 items-center gap-2">
                  <input
                    className={inputCls}
                    placeholder="https://…"
                    value={c.url}
                    onChange={(e) => updateCustom(idx, { url: e.target.value })}
                    data-testid={`social-custom-url-${idx}`}
                  />
                  <button onClick={() => removeLink((s) => s === c)} className="shrink-0 rounded-lg border border-border p-2 text-destructive hover:border-destructive" title="Remove channel" data-testid={`social-custom-remove-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={addCustom} className="inline-flex items-center gap-2 rounded-lg border border-navy/20 px-4 py-2 text-sm font-semibold text-navy hover:border-navy" data-testid="social-add-custom">
            <Plus className="h-4 w-4" /> Add another channel
          </button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-2 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-70" data-testid="social-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Social Media
          </button>
        </div>
      </div>
    </div>
  );
}
